/*
 * -----------------------------------------------------------------------
 *  <copyright file="heat-map-chart.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import React, { useState, useEffect, useRef, useContext } from 'react';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/modules/boost';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import {
    FormatNumberForChart,
    HC_COLORS_STANDARD,
    IOpraHeatMap,
    OpraColours,
    OpraVerticalAlign,
} from 'interfaces/opra-chart.interface';
import Loading from 'views/components/core/loading';
import { PanelFrameContext } from 'views/components/core/panel-frame';

const HeatMapChart = (chartProps: IOpraHeatMap) => {
    const { xAxis, yAxis, series, secondaryKey, showLegend, plotTitle, getRef, hidePlotTitle } = chartProps;
    const [options, setOptions] = useState<Highcharts.Options | null>(null);
    const charts = useRef<HighchartsReact.RefObject>(null);
    const height = useContext(PanelFrameContext);
    const [style, setStyle] = useState({
        style: {
            height: '350px',
        },
    });

    useEffect(() => {
        if (height?.height && height?.height !== style.style.height)
            setStyle({
                style: {
                    height: height?.height,
                },
            });
    }, [height?.height]);

    useEffect(() => {
        if (getRef && charts) getRef(charts.current);
    }, [charts.current, options]);

    const getData = () => {
        const array = [];

        for (let i = 0; i < xAxis.categories.length; i++) {
            for (let j = 0; j < yAxis.categories.length; j++) {
                array.push([i, j, series.data[yAxis.categories.length * i + j]]);
            }
        }
        return array;
    };

    useEffect(() => {
        const dataArrayLength = xAxis.categories.length * yAxis.categories.length;
        if (series?.data?.length !== dataArrayLength)
            throw Error(`Expecting ${dataArrayLength} data points but found ${series.data.length}`);

        const array: any[] = [...yAxis.categories].map((item: any) => item);
        const intArray: any[] = array.map((item: any) => +item || item);

        setOptions({
            boost: {
                enabled: true,
            },
            credits: {
                enabled: false,
            },
            chart: {
                type: 'heatmap',
                zoomType: 'xy',
                /* Removed scrolling fixes temporarily:
                scrollablePlotArea: {
                    minHeight: 27 * intArray.length,
                    opacity: 1
                }
                */
            },
            exporting: {
                enabled: !getRef,
                chartOptions: { title: { text: plotTitle, style: { fontWeight: 'semi-bold' } } },
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        enabled: false,
                    },
                },
            },
            title: {
                text: hidePlotTitle ? null : plotTitle,
            },
            subtitle:
                secondaryKey === undefined
                    ? undefined
                    : {
                        text: secondaryKey.text,
                        useHTML: true,
                        verticalAlign:
                            secondaryKey.position === OpraVerticalAlign.top
                                ? 'top'
                                : secondaryKey.position === OpraVerticalAlign.middle
                                    ? 'middle'
                                    : 'bottom',
                        style: { display: showLegend ?? true ? undefined : 'none' },
                    },
            legend: {
                enabled: showLegend ?? true,
                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    cursor: 'pointer',
                    fontWeight: 'normal',
                    fontSize: '12px',
                    textOverflow: 'ellipsis',
                },
            },
            colorAxis: {
                labels: {
                    style: {
                        fontSize: '12px',
                    },
                },
            },
            xAxis: {
                categories: [...xAxis.categories],
                title: {
                    text: xAxis.name,
                },
            },
            yAxis: {
                title: {
                    text: yAxis.name,
                },
                labels: {
                    formatter: (event: any) => array[event.pos],
                },
                // tickPixelInterval: 5,
                tickInterval: 1,
                max: intArray.length,
                scrollbar: {
                    enabled: true,
                },
            },
            colorAxis: {
                stops: [
                    [0, '#ffffff'],
                    [0.1, '#ffffbf'],
                    [0.5, '#f5a623'],
                    [0.8, '#d0021b'],
                    [1, '#d0021b'],
                ],
                min: 0 /* Maybe this should be an option on the panel? -- Math.min(...chartProps?.series?.data) */,
                max: Math.max(...(series ? series.data : [0])),
            },
            tooltip: {
                formatter(): any {
                    const self: any = this;
                    let result = `<b>${self.point?.series.xAxis.categories[self.point.x]}</b><br/>`;
                    result += `${self.point?.series.yAxis.userOptions.title.text}: `;
                    result += `${yAxis.categories[self.point.y]}<br/>`;
                    result += `Value: ${FormatNumberForChart(self.point.value)}`;
                    return result;
                },
            },
            series: [
                {
                    name: series.name,
                    data: getData(),
                },
            ],
        });
    }, [chartProps]);

    if (options === null) return <Loading />;

    return (
        <HighchartsReact
            ref={charts}
            highcharts={Highcharts}
            constructorType="chart"
            options={options}
            containerProps={style} // Mending size after fullscreen and setting with for expand chart functionality
        />
    );
};

export default HeatMapChart;
